import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { LocCity, Locations } from '../lib/types';

/**
 * Real maps: OpenStreetMap tiles under Leaflet.
 *
 * The public tile server is fine for a team's daily use; its policy asks heavy
 * users to run their own or pay a provider. VITE_MAP_TILES points the console
 * at one (any `{z}/{x}/{y}` URL) without a code change. If the tiles cannot be
 * reached — an office network that blocks them — the pins still draw, on a
 * blank background, in the right places relative to each other.
 */
export const TILES = {
  url: (import.meta.env.VITE_MAP_TILES as string | undefined) ?? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
};

/** Where a map opens before anything is chosen: Dubai, where the product began. */
export const DEFAULT_CENTRE: [number, number] = [25.2048, 55.2708];

/**
 * A Leaflet map in a div, created once and removed on unmount. Drawers slide
 * in, so the size is measured again once they have settled; without that the
 * map draws into the width it had mid-animation and leaves grey tiles.
 */
export function useLeafletMap(el: React.RefObject<HTMLDivElement>, centre: [number, number], zoom: number): L.Map | null {
  const [map, setMap] = useState<L.Map | null>(null);
  useEffect(() => {
    if (!el.current) return undefined;
    const m = L.map(el.current, { center: centre, zoom, zoomControl: true, attributionControl: true });
    L.tileLayer(TILES.url, { attribution: TILES.attribution, maxZoom: 19 }).addTo(m);
    const settle = window.setTimeout(() => m.invalidateSize(), 250);
    const resize = new ResizeObserver(() => m.invalidateSize());
    resize.observe(el.current);
    setMap(m);
    return () => { window.clearTimeout(settle); resize.disconnect(); m.remove(); setMap(null); };
    // the map is created once; later centres are applied with setView/fitBounds by the caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return map;
}

/* ------------------------------------------------------- choosing a place */

export interface PlaceValue { cityId: string | null; districtId: string | null }

/** The city a place sits in, by id, or by name for a property filed before the lists existed. */
export function findCity(locations: Locations | null, cityId: string | null, cityName?: string): LocCity | null {
  const all = locations?.countries.flatMap((k) => k.cities) ?? [];
  return all.find((c) => c.id === cityId)
    ?? (cityName ? all.find((c) => c.name.toLowerCase() === cityName.trim().toLowerCase()) : undefined)
    ?? null;
}

/**
 * Country, city and district, chosen from the places of operation the
 * BurjManage team keeps in the admin console. Districts arrive with the city:
 * they are fetched from OpenStreetMap when the city is added there.
 */
export function PlaceFields({ locations, value, onChange, idPrefix }: {
  locations: Locations | null; value: PlaceValue; onChange: (v: PlaceValue) => void; idPrefix: string;
}): React.JSX.Element {
  const countries = locations?.countries ?? [];
  const city = findCity(locations, value.cityId);
  const [countryCode, setCountryCode] = useState<string>(
    countries.find((k) => k.cities.some((c) => c.id === value.cityId))?.code ?? countries[0]?.code ?? '');
  const country = countries.find((k) => k.code === countryCode) ?? null;

  if (!locations) return <div className="hint">Loading places…</div>;
  if (countries.length === 0) {
    return (
      <div className="note warn">
        No places of operation are set up yet. Ask BurjManage to add your country and city in the admin console.
      </div>
    );
  }
  return (
    <div className="frow" style={{ gridTemplateColumns: countries.length > 1 ? '1fr 1fr 1.3fr' : '1fr 1.3fr' }}>
      {countries.length > 1 && (
        <div className="field">
          <label htmlFor={`${idPrefix}-country`}>Country</label>
          <select id={`${idPrefix}-country`} value={countryCode}
            onChange={(e) => { setCountryCode(e.target.value); onChange({ cityId: null, districtId: null }); }}>
            {countries.map((k) => <option key={k.code} value={k.code}>{k.name}</option>)}
          </select>
        </div>
      )}
      <div className="field">
        <label htmlFor={`${idPrefix}-city`}>City{countries.length === 1 ? ` · ${countries[0]!.name}` : ''}</label>
        <select id={`${idPrefix}-city`} value={value.cityId ?? ''}
          onChange={(e) => onChange({ cityId: e.target.value || null, districtId: null })}>
          <option value="">Choose…</option>
          {country?.cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`${idPrefix}-district`}>District</label>
        <select id={`${idPrefix}-district`} value={value.districtId ?? ''} disabled={!city}
          onChange={(e) => onChange({ ...value, districtId: e.target.value || null })}>
          <option value="">{!city ? 'Choose the city first' : city.districts.length ? 'Choose… (type to jump)' : 'None listed yet'}</option>
          {city?.districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- placing a pin */

export interface Pin { lat: number; lng: number }

/** "25.0805, 55.1403" — what Google Maps and most map apps copy — or the two numbers alone. */
export function parsePin(text: string): Pin | null {
  const m = /^\s*(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*$/.exec(text);
  if (!m) return null;
  const lat = Number(m[1]), lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

const pinIcon = L.divIcon({ className: 'pickpin', html: '<span></span>', iconSize: [26, 34], iconAnchor: [13, 33] });

/**
 * Where it is: click the map, drag the pin, or paste coordinates. The map opens
 * on the chosen district, else the city, so the building is a click away rather
 * than a search across a country.
 */
export function PinPicker({ value, onChange, locations, place, required }: {
  value: Pin | null; onChange: (p: Pin | null) => void;
  locations: Locations | null; place: PlaceValue; required?: boolean;
}): React.JSX.Element {
  const el = useRef<HTMLDivElement>(null);
  const map = useLeafletMap(el, value ? [value.lat, value.lng] : DEFAULT_CENTRE, value ? 17 : 11);
  const marker = useRef<L.Marker | null>(null);
  const [text, setText] = useState(value ? `${value.lat}, ${value.lng}` : '');
  const latest = useRef(onChange);
  latest.current = onChange;

  // the pin follows the value; a click or a drag sets the value
  useEffect(() => {
    if (!map) return;
    const click = (e: L.LeafletMouseEvent): void => {
      latest.current({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) });
    };
    map.on('click', click);
    return () => { map.off('click', click); };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    if (!value) { marker.current?.remove(); marker.current = null; return; }
    if (!marker.current) {
      marker.current = L.marker([value.lat, value.lng], { icon: pinIcon, draggable: true }).addTo(map);
      marker.current.on('dragend', () => {
        const p = marker.current!.getLatLng();
        latest.current({ lat: round(p.lat), lng: round(p.lng) });
      });
    } else {
      marker.current.setLatLng([value.lat, value.lng]);
    }
    setText(`${value.lat}, ${value.lng}`);
  }, [map, value]);

  // choosing a city or district moves the view there, unless a pin is already placed
  const city = useMemo(() => findCity(locations, place.cityId), [locations, place.cityId]);
  const district = city?.districts.find((d) => d.id === place.districtId) ?? null;
  useEffect(() => {
    if (!map || value) return;
    if (district?.latitude != null && district.longitude != null) {
      map.setView([district.latitude, district.longitude], 15);
    } else if (city?.latitude != null && city.longitude != null) {
      map.setView([city.latitude, city.longitude], 11);
    }
    // only when the place changes, not when the pin does
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, city?.id, district?.id]);

  const typed = parsePin(text);
  return (
    <div className="field">
      <label>Location{required ? '' : ' (optional)'}</label>
      <div ref={el} className="pickmap" />
      <div className="frow" style={{ gridTemplateColumns: '1fr auto', marginTop: 8, alignItems: 'center' }}>
        <input aria-label="Coordinates" inputMode="decimal" placeholder="25.0805, 55.1403" value={text}
          onChange={(e) => {
            setText(e.target.value);
            const p = parsePin(e.target.value);
            if (p) { onChange(p); map?.setView([p.lat, p.lng], 17); }
          }} />
        {value && !required && (
          <button type="button" className="btn sm quiet" onClick={() => { onChange(null); setText(''); }}>Clear</button>
        )}
      </div>
      <div className={`hint ${text && !typed ? 'late' : ''}`}>
        {text && !typed
          ? 'Latitude, then longitude: two numbers, such as 25.0805, 55.1403.'
          : value
            ? 'Drag the pin to adjust it.'
            : 'Click the building on the map, or paste coordinates from any map app’s “share location”.'}
      </div>
    </div>
  );
}

const round = (v: number): number => Math.round(v * 1e6) / 1e6;
